import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import {
  MeetingBooksLinkResponse,
  MeetingBookingAvailabilityResponse,
  MeetingBookingDurationResponse,
  MeetingBookingFormFieldResponse,
  MeetingHolidayResponse,
  MeetingUserAvailabilityOverrideResponse,
  PublicBookingRequest,
  PublicBookingResponse,
  PublicBusyInterval,
} from '../models/meeting.model';

/**
 * API pública del booking page (/book/:slug) — endpoints sin JWT bajo
 * /api/public/booking/**. Todo se resuelve por slug; el visitante anónimo
 * nunca necesita IDs internos.
 */
@Injectable({ providedIn: 'root' })
export class PublicBookingApiService {
  private url = `${environment.apiUrl}api/public/booking`;

  constructor(private http: HttpClient) {}

  getLink(slug: string): Observable<MeetingBooksLinkResponse> {
    return this.http.get<MeetingBooksLinkResponse>(`${this.url}/${slug}`);
  }

  listAvailability(slug: string): Observable<MeetingBookingAvailabilityResponse[]> {
    return this.http.get<MeetingBookingAvailabilityResponse[]>(
      `${this.url}/${slug}/availability`
    );
  }

  listDurations(slug: string): Observable<MeetingBookingDurationResponse[]> {
    return this.http.get<MeetingBookingDurationResponse[]>(
      `${this.url}/${slug}/durations`
    );
  }

  listFormFields(slug: string): Observable<MeetingBookingFormFieldResponse[]> {
    return this.http.get<MeetingBookingFormFieldResponse[]>(
      `${this.url}/${slug}/form-fields`
    );
  }

  listHolidays(slug: string): Observable<MeetingHolidayResponse[]> {
    return this.http.get<MeetingHolidayResponse[]>(`${this.url}/${slug}/holidays`);
  }

  /**
   * INC 7: días bloqueados (overrides no disponibles) del organizador del link.
   * Devuelve SOLO los días con isAvailable=false; startTime/endTime/reason pueden ser null.
   */
  listAvailabilityOverrides(slug: string): Observable<MeetingUserAvailabilityOverrideResponse[]> {
    return this.http.get<MeetingUserAvailabilityOverrideResponse[]>(
      `${this.url}/${slug}/availability-overrides`
    );
  }

  /**
   * INC 76: franjas ocupadas del organizador entre dos instantes, YA expandidas con el
   * margen (buffer) configurado en la página. La página solo tiene que descartar los
   * horarios que se solapen; la regla sigue viviendo en el backend.
   */
  listBusy(slug: string, fromIso: string, toIso: string): Observable<PublicBusyInterval[]> {
    const params = new HttpParams().set('from', fromIso).set('to', toIso);
    return this.http.get<PublicBusyInterval[]>(`${this.url}/${slug}/busy`, { params });
  }

  book(slug: string, body: PublicBookingRequest): Observable<PublicBookingResponse> {
    return this.http.post<PublicBookingResponse>(`${this.url}/${slug}/book`, body);
  }
}
